import { UserAuditLogEntry } from '../types/auditLogs.types';
import { EnrichmentMaps } from '../types/enrichment.types';

/**
 * Resolves the user ids referenced by a user audit-log entry to human-readable
 * names using the enrichment maps: the entry's author (so attributable events
 * can show "by <name>"), a conversation-assigned entry's requester, and an
 * agent ban/unban entry's target end user. Conversation-created and -assigned
 * entries also get the conversation's current details.
 */
export function enrichUserAuditLogEntry(
  entry: UserAuditLogEntry,
  { users, conversations }: EnrichmentMaps,
): UserAuditLogEntry {
  const authorName = users[entry.author.id]?.name;
  const enriched = authorName
    ? { ...entry, author: { ...entry.author, name: authorName } }
    : entry;

  const conversationDetails = (conversationId: string) => {
    const conversation = conversations[conversationId];
    return {
      conversation_state: conversation?.state,
      assigned_agent_id: conversation?.assigned_agent_id,
      assigned_agent_name: users[conversation?.assigned_agent_id ?? '']?.name,
    };
  };

  if (
    enriched.activity_type === 'CONVERSATION_ASSIGNED' &&
    enriched.contact_id
  ) {
    return {
      ...enriched,
      ...conversationDetails(enriched.conversation_id),
      contact_name: users[enriched.contact_id]?.name,
    };
  }

  if (enriched.activity_type === 'CONVERSATION_CREATED') {
    const conversation = conversations[enriched.conversation_id];
    return {
      ...enriched,
      ...conversationDetails(enriched.conversation_id),
      queue_name: conversation?.queue?.name,
      last_message_created_at: conversation?.last_message_created_at,
      pending_expiration_at: conversation?.pending_expiration_at,
    };
  }

  if (
    (enriched.activity_type === 'AGENT_BANNED_USER' ||
      enriched.activity_type === 'AGENT_UNBANNED_USER') &&
    enriched.user_id
  ) {
    return { ...enriched, user_name: users[enriched.user_id]?.name };
  }

  return enriched;
}
